<script setup>
  import {computed} from "vue";

  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import {Link as InertiaLink, router} from "@inertiajs/vue3";
  import {doctorProfession} from "@pages/Contact/Enums/Enums.js";
  import {MedButton, MedInfoButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ContactsContactRow",
  });

  const props = defineProps({
    contact: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["open:deleteContact"]);

  const filteredPatientsUrl = computed(() => {
    return (
      route("patients.index") +
      "?flags[]=" +
      encodeURIComponent("related-contact") +
      "&flags[]=" +
      encodeURIComponent("related-contact:" + props.contact.id)
    );
  });

  function deleteContact() {
    emit("open:deleteContact");
  }

  function clickRow(event, href) {
    let node = event.target ?? event;

    if (!node.nodeName) {
      return;
    }

    if (node.nodeName.toLowerCase() === "td") {
      // click on <td> => open link
      router.visit(href);
      return;
    }

    if (
      node.nodeName.toLowerCase() === "a" ||
      node.nodeName.toLowerCase() === "button" ||
      node.nodeName.toLowerCase() === "select" ||
      node.nodeName.toLowerCase() === "input" ||
      node.nodeName.toLowerCase() === "textarea" ||
      node.nodeName.toLowerCase() === "dialog"
    ) {
      // click on a clickable element (a or button)
      return;
    }

    if (node.parentNode) {
      // click on any other element: check if parent is <td>
      return clickRow(node.parentNode, href);
    }
  }
</script>

<template>
  <tr
    class="cursor-pointer border-b-2 border-gray-200 leading-tight hover:bg-gray-100"
    :data-test="getTestId('row-inertia-link')"
    @click="(event) => clickRow(event, $route('contacts.edit', {contact: contact.id}))"
  >
    <td class="px-0.5 py-2">
      <MedTooltip v-if="doctorProfession[contact.profession]" content="Fachrichtung">
        <span class="text-sm">{{ doctorProfession[contact.profession] }}</span>
      </MedTooltip>

      <div>
        <MedLink
          class="text-lg font-semibold"
          :data-test="getTestId('row-name-edit-inertia-link')"
          :href="$route('contacts.edit', {contact: contact.id})"
        >
          {{ contact.title }}

          {{ contact.lastname ? contact.lastname + "," : "" }} {{ contact.firstname }}
        </MedLink>
      </div>
    </td>

    <td class="px-0.5 py-2">
      <MedLink
        :href="$route('contacts.edit', {contact: contact.id})"
        :data-test="getTestId('row-company-edit-inertia-link')"
      >
        {{ contact.company }}
      </MedLink>
    </td>

    <td class="px-0.5 py-2">
      <div class="flex items-center space-x-2">
        <!-- related patients -->
        <MedTooltip content="Zugeordnete Patienten anzeigen" :disabled="contact.patients_count === 0">
          <ComponentCategoryBadge class="min-h-5" :color="contact.patients_count > 0 ? 'info' : null">
            <component
              :is="contact.patients_count === 0 ? 'span' : InertiaLink"
              :href="filteredPatientsUrl"
              class="text-gray-900"
              :data-test="getTestId(`filtered-patients-url-${contact.patients_count === 0 ? 'span' : 'inertia-link'}`)"
            >
              {{ contact.patients_count }} {{ contact.patients_count === 1 ? "Patient" : "Patienten" }} zugeordnet
            </component>
          </ComponentCategoryBadge>
        </MedTooltip>

        <!-- contacts.note -->
        <MedInfoButton
          v-if="contact.note"
          size="lg"
          side="top"
          align="end"
          variant="secondary"
          aria-label="Zusätzliche Informationen"
          :data-test="getTestId('row-note-info-button')"
        >
          <p class="text-sm"><strong>Hinterlegte zusätzliche Informationen:</strong></p>
          <p class="text-sm">{{ contact.note }}</p>
        </MedInfoButton>
      </div>
    </td>

    <td class="hidden px-0.5 py-2 2xl:table-cell">
      {{ contact.email }}
    </td>

    <td class="hidden px-0.5 py-2 2xl:table-cell">
      {{ contact.telephone }}
    </td>

    <!-- TODO remove tooltip - change header like name/lastname -->
    <td class="hidden px-0.5 py-2 lg:table-cell">
      <div v-if="contact.street">
        <MedTooltip content="Straße, Hausnr.">
          <span>
            {{ contact.street }}
          </span>
        </MedTooltip>
      </div>

      <div v-if="contact.zip || contact.city">
        <MedTooltip content="Ort">
          <span>{{ contact.zip }} {{ contact.city }}</span>
        </MedTooltip>
      </div>
    </td>

    <!-- actions -->
    <td class="px-0.5 py-2">
      <div class="flex flex-row-reverse justify-center gap-4 space-x-reverse">
        <MedButton
          :aria-label="contact.lastname ? `Kontakt ${contact.lastname} löschen` : 'Kontakt löschen'"
          flat
          icon="delete"
          rounded="full"
          :data-test="getTestId('row-delete-button')"
          @click="deleteContact"
        />

        <MedButton
          :aria-label="contact.lastname ? `Kontakt ${contact.lastname} bearbeiten` : 'Kontakt bearbeiten'"
          flat
          as="inertia"
          variant="primary"
          icon="edit"
          rounded="full"
          :data-test="getTestId('row-edit-button')"
          :href="$route('contacts.edit', {contact: contact.id})"
        />
      </div>
    </td>
  </tr>
</template>
